import React, { useMemo } from "react";

import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import NotFound from "Components/NotFound";
import Loading from "ds/PageLoading";
import * as Tabs from "ds/Tabs";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { selectEnvironmentDomainsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import * as S from "./settings.style";

const EnvironmentSettingsLayout = styled.div`
  width: 100%;
  min-height: calc(100vh - 172px);
  background: transparent;
  > div {
    background: transparent;
  }
`;

const CustomTabs = styled(Tabs.Root)`
  &[data-orientation="horizontal"] {
    position: absolute;
    top: 56px;
    z-index: ${props => (props.$showTabs ? 5 : -1)};
  }
`;

const EnvironmentSettings = ({ children, entries = [] }) => {
  const { organizationId, projectId, environmentId } = useDecodedParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const isLoadingEnvironment = useSelectorWithUrlParams(
    environmentLoadingSelector
  );

  const showTabs =
    pathname.endsWith("/variables") ||
    pathname.endsWith("/settings") ||
    pathname.endsWith("/domains");

  const environmentDomainsEnabled = useAppSelector(
    selectEnvironmentDomainsEnabled
  );

  const tabs = useMemo(() => {
    const envId = encodeURIComponent(environmentId);
    const basePath = `/${organizationId}/${projectId}/${envId}`;
    const settingsPath = `${basePath}/settings`;

    const settingsTabs = [
      {
        name: "general",
        route: settingsPath,
        permission: "#edit"
      },
      {
        name: "variables",
        route: `${settingsPath}/variables`,
        permission: "#manage-variables"
      },
      {
        name: "domains",
        route: `${settingsPath}/domains`,
        permission: "#manage-variables"
      }
    ];

    return settingsTabs
      .filter(elt => entries.includes(elt.name))
      .filter(tab =>
        !environmentDomainsEnabled ? tab.name !== "domains" : tab
      );
  }, [organizationId, projectId, environmentId, entries]);

  return environment ? (
    <EnvironmentSettingsLayout className="settings-content">
      <CustomTabs defaultValue={pathname} $showTabs={showTabs}>
        <Tabs.List role="tablist">
          {tabs.map(tab => (
            <Tabs.Trigger
              data-testid={tab.name}
              onClick={() => navigate(tab.route)}
              key={tab.route}
              value={tab.route}
              role="tab"
              aria-selected={false}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(tab.route);
                }
              }}
            >
              <FormattedMessage id={`tabs.${tab.name}`} />
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </CustomTabs>
      {children}
    </EnvironmentSettingsLayout>
  ) : isLoadingEnvironment ? (
    <Loading iconOnly />
  ) : (
    <S.NotExistWrapper>
      <NotFound
        description={intl.formatMessage({
          id: "environment_deleted_or_inactive"
        })}
      />
    </S.NotExistWrapper>
  );
};

EnvironmentSettings.propTypes = {
  children: PropTypes.node,
  entries: PropTypes.array
};

export default EnvironmentSettings;
